import React, { useState, useEffect } from 'react';
import { Award, Users, Building, Clock, CheckCircle, Star, MessageCircle } from 'lucide-react';

const AboutPage = () => {
  const [statsAnimated, setStatsAnimated] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const statsSection = document.getElementById('stats-section');
      if (statsSection && !statsAnimated) {
        const rect = statsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          setStatsAnimated(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [statsAnimated]);

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/919884611368?text=Hello, I would like to know more about NPSH Developers', '_blank');
  };

  const teamMembers = [
  {
    name: "Mr. N. Subramanian",
    role: "Managing Director",
    image: "pics/team/subramanian.webp", // Use your actual image location here
    experience: "15+ Years",
    description: "Visionary leader with handful of experience"
  },
  {
    name: "Mrs. S. Dhanalakshmi",
    role: "Director",
    image: "pics/team/avatar.webp",
    experience: "15+ Years",
    description: "Director of operations and client relations, ensuring smooth execution of residential projects."
  },
  {
    name: "Mrs. Florence Christopher",
    role: "Manager",
    image: "pics/team/icon.webp",
    experience: "15+ Years",
    description: "Manager overseeing daily company operations with an eye for detail and customer care."
  },
  {
    name: "Mr. N. Augustin",
    role: "Project Manager",
    image: "pics/team/avatar.webp",
    experience: "30+ Years",
    description: "Project planning and field coordination to ensure timely and cost-effective execution."
  },
  {
    name: "Mr. Chakravarthy",
    role: "Site Engineer",
    image: "pics/team/avatar.webp",
    experience: "3+ Years",
    description: "Experienced in infrastructure project delivery and on-site leadership across residential builds."
  },
  {
    name: "Mrs. Angelin",
    role: "Head of Procurement",
    image: "pics/team/icon.webp",
    experience: "3+ Years",
    description: "Experienced in procurement strategies and vendor management for residential projects."
  }
];

  const values = [
  {
    icon: CheckCircle,
    title: "Integrity",
    description: "Honest communication and transparent operations form the foundation of every project."
  },
  {
    icon: Clock,
    title: "On-Time Delivery",
    description: "We consistently meet deadlines through efficient project planning and execution."
  },
  {
    icon: Users,
    title: "Client-Centered",
    description: "We build homes tailored to customer needs, with full satisfaction guaranteed."
  },
  {
    icon: Award,
    title: "Design Excellence",
    description: "Our partnership with leading architects ensures smart and elegant construction."
  }
];


  const milestones = [
  { year: "2017", event: "NSPH Developers Pvt Ltd Founded", description: "Established with a vision to build reliable residential spaces across Chennai." },
  { year: "2018", event: "First Villa Project Completed", description: "Delivered premium independent villas in Chennai's west zone." },
  { year: "2020", event: "Partnership with Blue Cube Architects", description: "Collaborated with renowned architect Mr. G. Harish for design-led growth." },
  { year: "2022", event: "Expanded Across Chennai", description: "Completed multiple residential apartments and bungalows across the city." },
  { year: "2024", event: "Recognized as a Trusted Builder", description: "Established strong reputation in Tamil Nadu’s competitive real estate market." }
];


  const achievements = [
  { icon: Building, label: "Projects Delivered", value: "25+", color: "text-yellow-600" },
  { icon: Users, label: "Happy Clients", value: "500+", color: "text-yellow-600" },
  { icon: Clock, label: "Years in Service", value: "7+", color: "text-yellow-600" },
  { icon: Star, label: "Partnerships", value: "10+", color: "text-yellow-600" }
];


  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-yellow-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-6xl font-bold text-yellow-600 mb-6">
              About NPSH Developers
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              For over 12 years, we have been Chennai's trusted partner in creating exceptional 
        residential and commercial spaces that blend modern architecture with traditional values.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-800">Our Story</h2>
              <p className="text-gray-600 leading-relaxed">
               NPSH Developers Pvt Ltd was founded in 2017 with a mission to provide high-quality,
          budget-friendly homes for 100+ families across Chennai. Since then, we've grown into a
          trusted name, known for our professionalism, timely delivery, and customer satisfaction.
        </p>
            
              <p className="text-gray-600 leading-relaxed">
               With a strong leadership team, dedicated project managers, and creative partnerships
          with leading architects, NPSH Developers has delivered multiple residential projects,
          from villas to apartment complexes, across Tamil Nadu. Our commitment to
          transparency, safety, and innovation makes us a preferred builder in the region.
        </p>
              
              <button
                onClick={handleWhatsAppClick}
                className="inline-flex items-center px-8 py-4 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Connect With Us
              </button>
            </div>
            <div className="relative">
              <img
                src="pics/logo full.png"
                alt="NPSH Developers Office"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl border-4 border-yellow-100"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Stats */}
      <section id="stats-section" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-yellow-600 mb-4">Our Achievements</h2>
            <div className="w-24 h-1 bg-yellow-600 mx-auto rounded"></div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => {
              const IconComponent = achievement.icon;
              return (
                <div
                  key={index}
                  className={`text-center p-8 bg-gray-50 rounded-2xl border border-gray-100 transform transition-all duration-700 ${
                    statsAnimated ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="mb-4 flex justify-center">
                    <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center">
                      <IconComponent className={`h-8 w-8 ${achievement.color}`} />
                    </div>
                  </div>
                  <p className="text-4xl font-bold text-gray-800 mb-2">{achievement.value}</p>
                  <p className="text-gray-600 font-medium">{achievement.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-yellow-600 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do and define who we are as a company.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">


            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100 hover:border-yellow-600 transition-all duration-300 text-center group hover:shadow-xl"
                >
                  <div className="w-20 h-20 bg-yellow-100 group-hover:bg-yellow-600 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-colors duration-300">
                    <IconComponent className="h-10 w-10 text-yellow-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-yellow-600 transition-colors duration-200">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
<section className="py-20 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-16">
      <h2 className="text-4xl font-bold text-yellow-600 mb-4">Leadership Team</h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        Meet the visionaries behind NPSH Developers' continued success and growth.
      </p>
    </div>
    
    <div className="flex flex-col items-center mb-12">
      <img
        src={teamMembers[0].image}
        alt={teamMembers[0].name}
        className="w-96 h-96 object-cover border-4 border-yellow-400 shadow-xl mb-4 rounded-xl"
      />
      <div className="bg-yellow-600 text-white px-4 py-2 rounded-full text-sm font-medium -mt-6 mb-2 text-center">
        {teamMembers[0].experience}
      </div>
      <h3 className="text-2xl font-bold text-yellow-600 mt-2 text-center">{teamMembers[0].name}</h3>
      <p className="text-gray-800 font-medium mb-2 text-center">{teamMembers[0].role}</p>
      <p className="text-gray-600 text-base leading-relaxed max-w-lg text-center">{teamMembers[0].description}</p>
    </div>


    {/* Other team members below */}
    <div className="flex flex-row justify-center gap-4 px-2 overflow-x-auto sm:overflow-x-visible scrollbar-thin scrollbar-thumb-yellow-600 scrollbar-track-yellow-100" style={{ WebkitOverflowScrolling: 'touch' }}>
      {teamMembers.slice(1).map((member, index) => (
        <div
          key={index}
          className="bg-white p-2 w-44 rounded-xl shadow-md border border-gray-100 hover:border-yellow-600 transition-all duration-300 text-center group hover:shadow-lg flex-shrink-0"
        >
          <div className="relative mb-4">
            <img
              src={member.image}
              alt={member.name}
              className="w-16 h-16 rounded-full object-cover mx-auto border-2 border-yellow-100 group-hover:border-yellow-600 transition-colors duration-300"
            />
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-yellow-600 text-white px-2 py-1 rounded-full text-xs font-medium">
              {member.experience}
            </div>
          </div>
          <h3 className="text-lg font-bold text-yellow-600 mb-1">{member.name}</h3>
          <p className="text-gray-800 font-medium mb-2 text-sm">{member.role}</p>
          <p className="text-gray-600 text-xs leading-relaxed">{member.description}</p>
        </div>
      ))}
    </div>
  </div>
</section>


      {/* Company Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-yellow-600 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600">
              Milestones that mark our growth and commitment to excellence over the years.
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-yellow-600 rounded"></div>
            
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className={`relative flex items-center mb-12 ${
                  index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                }`}
              >
                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                  <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-yellow-100 hover:border-yellow-600 transition-all duration-300">
                    <h3 className="text-2xl font-bold text-yellow-600 mb-2">{milestone.year}</h3>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">{milestone.event}</h4>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </div>
                
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-yellow-600 rounded-full border-4 border-white shadow-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-600 to-yellow-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Build Your Dream?
          </h2>
          <p className="text-xl text-yellow-100 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who chose NPSH Developers for their dream homes and commercial spaces.
          </p>
          <button
            onClick={handleWhatsAppClick}
            className="inline-flex items-center px-8 py-4 bg-white text-yellow-600 font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            <MessageCircle className="mr-2 h-6 w-6" />
            Start Your Journey Today
          </button>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;